import { useState, useEffect } from "react";
import {
  Cog6ToothIcon,
  BellIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  ChatBubbleLeftRightIcon,
} from "@heroicons/react/24/outline";
import { ActivQMSLoader } from "../../components/ui/activqms-loader";
import { SlackConfiguration } from "../../components/slack/slack-configuration";
import { LinkedInConfiguration } from "../../components/linkedin/linkedin-configuration";

import { useTenantContext } from "../../hooks/use-tenant-context";

export function SettingsPage() {
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("integrations");
  const { isReady, tenantId, userRole } = useTenantContext();

  // Wait for tenant context to be ready
  useEffect(() => {
    if (isReady) {
      setIsLoading(false);
    }
  }, [isReady]);

  // Show loading state while tenant context loads
  if (isLoading || !isReady) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <ActivQMSLoader size="lg" text="Loading Settings..." />
      </div>
    );
  }

  // Check if user has permission to access settings
  const canAccessSettings =
    userRole === "SUPER_ADMIN" || userRole === "TENANT_ADMIN";

  if (!canAccessSettings) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <ShieldCheckIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            Access Denied
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            You don't have permission to access settings.
          </p>
        </div>
      </div>
    );
  }

  const tabs = [
    {
      id: "integrations",
      name: "Integrations",
      icon: ChatBubbleLeftRightIcon,
      description: "Connect external services",
    },
    {
      id: "notifications",
      name: "Notifications",
      icon: BellIcon,
      description: "Manage notification preferences",
    },
    {
      id: "security",
      name: "Security",
      icon: ShieldCheckIcon,
      description: "Security and access controls",
    },
    {
      id: "team",
      name: "Team",
      icon: UserGroupIcon,
      description: "Team management and roles",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center">
            <Cog6ToothIcon className="h-8 w-8 text-brand-600 mr-3" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
              <p className="mt-1 text-sm text-gray-600">
                Manage your activQMS configuration and integrations
              </p>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                      activeTab === tab.id
                        ? "border-brand-500 text-brand-600"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                  >
                    <Icon
                      className={`-ml-0.5 mr-2 h-5 w-5 ${
                        activeTab === tab.id
                          ? "text-brand-500"
                          : "text-gray-400 group-hover:text-gray-500"
                      }`}
                    />
                    <span>{tab.name}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {activeTab === "integrations" && (
            <div className="space-y-6">
              <div>
                <h2 className="text-lg font-medium text-gray-900 mb-2">
                  External Integrations
                </h2>
                <p className="text-sm text-gray-600 mb-6">
                  Connect activQMS with your favorite tools and services to
                  streamline your workflow.
                </p>
              </div>

              {/* Slack Integration */}
              <SlackConfiguration
                clientId={tenantId || ""}
                onConfigurationChange={() => {
                  console.log("Slack configuration updated");
                }}
              />

              {/* LinkedIn Ads Integration */}
              <LinkedInConfiguration
                clientId={tenantId || ""}
                onConfigurationChange={() => {
                  console.log("LinkedIn configuration updated");
                }}
              />

              {/* Placeholder for future integrations */}
              <div className="bg-white shadow rounded-lg border-2 border-dashed border-gray-300">
                <div className="px-4 py-8 text-center">
                  <ChatBubbleLeftRightIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    More integrations coming soon
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    We're working on adding more integrations like Microsoft
                    Teams, Discord, and more.
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeTab === "notifications" && (
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  Notification Preferences
                </h3>
                <p className="text-sm text-gray-600">
                  Notification settings will be available here soon.
                </p>
              </div>
            </div>
          )}

          {activeTab === "security" && (
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  Security Settings
                </h3>
                <p className="text-sm text-gray-600">
                  Security configuration options will be available here soon.
                </p>
              </div>
            </div>
          )}

          {activeTab === "team" && (
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  Team Management
                </h3>
                <p className="text-sm text-gray-600">
                  Team management features will be available here soon.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
